/**
 * Playground v2 — HR-friendly types.
 *
 * Deliberately simpler than playground/types.ts:
 *   - No verdict-rule traces, no weight arithmetic surfaced in UI
 *   - Scores are 0–10 (bars) or qualitative pills (Very Strong / Strong / Moderate / Weak)
 *   - A single headline "match" label per candidate (Strong / Moderate / Weak / — )
 *   - Editable scores with reason text (becomes an Override) + row-level comments
 *   - One unified Candidate object carries both full-talent data AND per-stage reports,
 *     so the UI can flip between them without refetching
 */

/* ─── Stages ─── */

export const STAGES = [
  'applied', 'cv_screen', 'ai_interview', 'interview', 'offer', 'hired',
] as const
export type Stage = typeof STAGES[number]

export const STAGE_LABELS: Record<Stage, string> = {
  applied: 'Applied',
  cv_screen: 'CV Screen',
  ai_interview: 'AI Interview',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
}

/* ─── Qualitative match ─── */

export type MatchLevel = 'very_strong' | 'strong' | 'moderate' | 'weak'

export const MATCH_LABELS: Record<MatchLevel, string> = {
  very_strong: 'Very strong',
  strong: 'Strong',
  moderate: 'Moderate',
  weak: 'Weak',
}

export const MATCH_PALETTE: Record<MatchLevel, { fg: string; bg: string }> = {
  very_strong: { fg: 'var(--fg-success)', bg: 'var(--bg-success)' },
  strong:      { fg: 'var(--fg-info)',    bg: 'var(--bg-info)' },
  moderate:    { fg: 'var(--fg-warning)', bg: 'var(--bg-warning)' },
  weak:        { fg: 'var(--fg-danger)',  bg: 'var(--bg-danger)' },
}

export type HeadlineMatch = 'strong_match' | 'match' | 'needs_review' | 'not_a_fit'
export const HEADLINE_MATCH: Record<HeadlineMatch, { label: string; tone: 'success' | 'info' | 'warning' | 'danger' }> = {
  strong_match: { label: 'Strong match', tone: 'success' },
  match:        { label: 'Match',         tone: 'info' },
  needs_review: { label: 'Needs review',  tone: 'warning' },
  not_a_fit:    { label: 'Not a fit',     tone: 'danger' },
}

/* ─── Score Override + Comment primitives ─── */

export interface Author {
  id: string
  name: string
  initials: string
  color: string
}

export interface Override {
  id: string
  author: Author
  at: string              // relative or ISO, display only
  /** For requirements: boolean. For criteria: 0–10 number. */
  newValue: boolean | number
  oldValue: boolean | number
  reason: string
}

export interface Comment {
  id: string
  author: Author
  at: string
  text: string
}

/* ─── Report data ─── */

export interface Requirement {
  id: string
  label: string
  passed: boolean
  note?: string
  overrides?: Override[]    // most recent last
  comments?: Comment[]
}

export interface RankingCriterion {
  id: string
  label: string
  score: number       // 0..10
  maxScore: number    // 10
  note?: string       // the one-line justification
  overrides?: Override[]
  comments?: Comment[]
}

export interface CVScreeningReport {
  kind: 'cv_screen'
  headlineScore: number            // e.g. 85 — one number, simple
  requirementsMet: number          // e.g. 5
  requirementsTotal: number        // e.g. 5
  processedAt: string              // 'Processed Mar 24'
  requirements: Requirement[]
  criteria: RankingCriterion[]
}

export interface AIInterviewReport {
  kind: 'ai_interview'
  headlineScore: number            // e.g. 82
  duration: string                 // '22m 14s'
  processedAt: string
  competencies: RankingCriterion[]   // same 0–10 shape as CV criteria (keeps UI consistent)
  qualitativeFit: {
    communication: MatchLevel
    depth: MatchLevel
    problemSolving: MatchLevel
    culture: MatchLevel
  }
  summary: string
}

export interface InterviewReport {
  kind: 'interview'
  scheduledAt?: string
  interviewer?: Author
  notes?: string
}

export interface TranscriptReport {
  kind: 'transcript'
  turns: TranscriptTurn[]
  duration: string
}

export interface TranscriptTurn {
  id: string
  speaker: 'interviewer' | 'candidate'
  at: string                        // '02:14'
  text: string
}

export type StageReport =
  | CVScreeningReport
  | AIInterviewReport
  | InterviewReport
  | TranscriptReport

/* ─── Full-talent sections (always-visible left panel) ─── */

export interface AIAssessment {
  headline: HeadlineMatch
  summary: string
  strengths: string[]
  growth: string[]
}

export interface ICPCriterion {
  id: string
  label: string
  level: MatchLevel
  note?: string
}

export interface StageStep {
  stage: Stage
  state: 'done' | 'active' | 'upcoming'
  date?: string    // 'Mar 23' or 'Today'
}

export interface ExperienceEntry {
  id: string
  company: string
  role: string
  start: string
  end: string
  summary?: string
}

export interface ActivityEvent {
  id: string
  kind: 'stage_change' | 'message' | 'note' | 'system' | 'override'
  text: string
  at: string
  by?: string
}

/* ─── Candidate (the source of truth for both kanban + report) ─── */

export interface Candidate {
  id: string
  name: string
  initials: string
  role: string
  company: string
  location?: string
  email?: string
  phone?: string
  linkedinUrl?: string
  avatarColor: string

  /* Pipeline */
  stage: Stage
  status: 'for_review' | 'in_progress' | 'accepted' | 'rejected' | 'on_hold'
  daysInStage: number

  /* Top-level signal — one number, one label */
  overallScore: number           // 0..100 — averaged across stages the candidate has been through (simple)
  headlineMatch: HeadlineMatch
  sources: string[]

  /* Always-visible talent view */
  ai: AIAssessment
  icp: ICPCriterion[]
  stepper: StageStep[]
  experience?: ExperienceEntry[]
  skills?: string[]
  activity?: ActivityEvent[]

  /* Per-stage reports — keyed by kind for quick lookup */
  reports: StageReport[]

  /* Notes + files live on the candidate (not a stage) */
  notes: CandidateNote[]
  files: CandidateFile[]
}

export interface CandidateNote {
  id: string
  author: Author
  at: string
  text: string
}

export interface CandidateFile {
  id: string
  name: string
  kind: 'pdf' | 'doc' | 'image' | 'other'
  size: string
  uploadedAt: string
}

/* ─── Board types ─── */

export interface Column {
  stage: Stage
  title: string
  candidates: Candidate[]
}

/* ─── Viewer mode toggle (subtle full-talent vs per-stage) ─── */

export type ReportFocus = 'talent' | 'stage'
